// @flow
import { get, reject } from 'lodash';
import URI from 'urijs';

import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { getTransportIcon } from 'Components/widgets/transport-details/transportUtils';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { hasChildcare, hasTransfer } from 'Utils/proposal/proposalUtils';
import { isWinterMountainResort } from 'Utils/resort/resortUtils';
import { convertLocationToURI } from 'Utils/uri/uriUtils';

const {
  PACKAGES: { JOYVIEW_ROOM_ONLY },
} = constants;
const BASE_PATH = '/booking/';

export const shouldHideServicesPage = (selectedProposal: Proposal, resort: Resort): boolean => {
  return (
    (!hasChildcare(selectedProposal) &&
      !resort.isJoyview &&
      selectedProposal.additionalServices.childcares.length === 0) ||
    (resort.isJoyview &&
      !selectedProposal.alternativePackages &&
      selectedProposal.packageId === JOYVIEW_ROOM_ONLY)
  );
};

export const shouldHideTransportPage = (selectedProposal: Proposal): boolean => {
  return !hasTransfer(selectedProposal) && !get(selectedProposal, 'transports');
};

type getFilteredBreadcrumbPagesReturnType = $ReadOnlyArray<BookingPageData>;

export const getStepIndex = (
  steps: getFilteredBreadcrumbPagesReturnType,
  bookingStep: string,
): number => steps.findIndex(step => step.name === bookingStep);

// eslint-disable-next-line max-params
export const getFilteredBreadcrumbPages = ({
  selectedProposal,
  resort,
  i18n,
  isActivitiesViewFeatureFlipped,
  isOptionHoldOnlyFeatureFlipped,
  isSeller = false,
}: {
  selectedProposal: Proposal,
  resort: Resort,
  i18n: CMSContent,
  isActivitiesViewFeatureFlipped: boolean,
  isOptionHoldOnlyFeatureFlipped: boolean,
  isSeller: boolean,
}): getFilteredBreadcrumbPagesReturnType => {
  const activitiesView =
    isActivitiesViewFeatureFlipped &&
    (selectedProposal.additionalServices.activities.some(
      activity => activity.productInfo.isIncluded === false,
    ) ||
      selectedProposal.additionalServices.rentals.length > 0)
      ? {
          name: 'activities',
          path: `${BASE_PATH}activities`,
          actionGA: 'activities',
          labelGA: 'validateActivities',
          basePriceGALabel: 'basePriceActivities',
          icon: isWinterMountainResort(resort) ? ICONS.SKI : ICONS.GOLF,
          showIcon: true,
          title: i18n.booking.activities.pageTitle,
          seoTitle: i18n.booking.activities.seo.title,
          gridSize: 'large',
        }
      : null;

  const isJoyview = get(resort, 'isJoyview');
  const breadcrumbPages = [
    {
      name: 'accommodation',
      path: `${BASE_PATH}accommodation`,
      eventPageName: 'accommodations',
      actionGA: 'accommodation',
      labelGA: 'validateAccommodation',
      basePriceGALabel: 'basePriceAccommodation',
      icon: ICONS.ACCOMODATIONS,
      showIcon: true,
      title: tAlt({
        resortKeyPath: 'booking.accommodation.pageTitle',
        cruiseKeyPath: 'booking.accommodation.pageTitleCruise',
        resort,
      }),
      seoTitle: i18n.booking.accommodation.seo.title,
      gridSize: 'large',
    },
    {
      name: 'transport',
      path: `${BASE_PATH}transport`,
      actionGA: 'transport',
      labelGA: 'validateTransport',
      ...(get(selectedProposal, 'transports')
        ? {
            icon: getTransportIcon(
              get(selectedProposal, 'transports.inbound'),
              get(selectedProposal, 'transports.outbound'),
            ),
            title: i18n.booking.transport.transportPageTitle,
            seoTitle: i18n.booking.transport.seo.title,
            basePriceGALabel: 'basePriceTransport',
          }
        : {
            icon: ICONS.TRANSFER,
            title: i18n.booking.transport.transferPageTitle,
            seoTitle: i18n.booking.transport.transfer.seo.title,
            basePriceGALabel: 'basePriceTransfer',
          }),
      showIcon: true,
      isToBeHidden: shouldHideTransportPage(selectedProposal),
    },
    {
      name: 'services',
      path: `${BASE_PATH}services`,
      actionGA: isJoyview ? 'allInclusive' : 'service',
      labelGA: isJoyview ? 'validateAllInclusive' : 'validateServices',
      basePriceGALabel: 'basePriceServices',
      icon: isJoyview ? ICONS.DATE_PLUS : ICONS.CHILDREN,
      showIcon: true,
      title: isJoyview
        ? i18n.booking.services.pageTitleJoyview
        : i18n.booking.services.pageTitleChildcare,
      seoTitle: i18n.booking.services.seo.title,
      isToBeHidden: shouldHideServicesPage(selectedProposal, resort),
      gridSize: isJoyview ? 'small' : 'large',
    },
    {
      name: 'participants',
      path: `${BASE_PATH}participants`,
      actionGA: 'participant',
      labelGA: 'validateParticipants',
      basePriceGALabel: 'basePricePaymentParticipants',
      icon: ICONS.CONTACT,
      showIcon: true,
      title: i18n.booking.participants.pageTitle,
      seoTitle: i18n.booking.participants.seo.title,
      gridSize: 'mini',
    },
    {
      name: 'summary',
      path: `${BASE_PATH}summary`,
      actionGA: 'summary',
      labelGA: 'validateSummary',
      basePriceGALabel: 'basePriceSummary',
      icon: ICONS.SUMMARY,
      showIcon: true,
      title: i18n.booking.summary.pageTitle,
      seoTitle: i18n.booking.summary.seo.title,
      gridSize: 'large',
    },
  ];
  if (activitiesView) breadcrumbPages.splice(3, 0, activitiesView);

  // adds the banking step if the optionHoldOnly featureFlip is disabled
  if (!isOptionHoldOnlyFeatureFlipped)
    breadcrumbPages.push({
      name: 'banking',
      path: `${BASE_PATH}banking`,
      actionGA: 'banking',
      labelGA: 'validateBanking',
      basePriceGALabel: 'basePricePaymentBanking',
      icon: ICONS.CREDIT_CARD,
      showIcon: !isSeller,
      title: i18n.booking.banking.pageTitle,
      seoTitle: i18n.booking.banking.seo.title,
    });

  // $FlowFixMe Absolutely no clue of why
  return reject(breadcrumbPages, 'isToBeHidden');
};

type NavigateInBookingEngineProps = {
  location: BookingRouterLocation | URI,
  history: RouterHistory,
  replace?: boolean,
};
export const navigateInBookingEngine = ({
  location,
  history,
  replace = false,
}: NavigateInBookingEngineProps) => {
  const nextUri = location instanceof URI ? location : convertLocationToURI(location);
  const fileName = nextUri.filename();
  const shownUri = nextUri.clone();
  nextUri.pathname('/booking').setQuery('bookingStep', fileName);

  const nextUrl = nextUri.resource().toString();
  const shownUrl = shownUri.resource().toString();

  if (replace) {
    history.replace(nextUrl, shownUrl);
  } else {
    history.push(nextUrl, shownUrl);
  }
};

export const getAllProposalsIdWithAlternative = (proposal: Proposal) => {
  return [
    proposal.id,
    ...(proposal.alternativePackages
      ? proposal.alternativePackages.map(alternativePackage => alternativePackage.proposalId)
      : []),
  ];
};
